<template>
  <transition name="transition-scale-fade">
    <div
      v-if="isShowing"
      class="terms-acceptance-reminder card roomy text-left with-border"
      style="position: fixed; bottom: 100px; right: 0; max-width: 40rem; z-index: var(--depth-3); margin-inline: var(--spacing-lg)"
    >
      <h2 class="h4 margin-top-0">Review and Accept Our Terms</h2>
      <p>Greetings, mellow Font Awesome citizen! It appears that you haven't accepted our most recent policies and terms of service.</p>

      <div class="margin-y-xl">
        <owa-accept-policies-checkbox v-model="acceptPolicies" />

        <template v-if="shouldShowMarketingOptIn">
          <label
            for="opt-into-marketing"
            class="margin-x-0 margin-y-sm"
            style="--label-font-size: var(--font-size-sm); --link-color: var(--fa-navy); --label-font-weight: normal; display: flex; gap: var(--spacing-3xs)"
          >
            <input
              id="opt-into-marketing"
              name="opt-into-marketing"
              type="checkbox"
              :checked="optIntoMarketing"
              @change="(event) => (optIntoMarketing = event.target.checked)"
            />

            <span>
              Send me Font Awesome emails, or
              <site-link
                :to="{ name: 'account.settings', hash: '#email-prefs' }"
                class="strong"
                >let me adjust my email preferences</site-link
              >.
            </span>
          </label>
        </template>
        <template v-else>
          <p
            class="margin-top-2xl size-sm"
            style="--label-font-size: var(--font-size-sm); --link-color: var(--fa-navy)"
          >
            And, if you like, you can
            <site-link
              :to="{ name: 'account.settings', hash: '#email-prefs' }"
              class="strong"
              >adjust your email marketing preferences</site-link
            >
            in your account.
          </p>
        </template>

        <owa-message
          v-if="errorMessage"
          class="margin-top-md"
          theme="danger"
          type="flash"
          :content="errorMessage"
        />
      </div>

      <div
        class="display-flex flex-column align-between tablet:flex-row tablet:flex-items-center margin-top-lg"
        style="gap: var(--spacing-md)"
      >
        <a
          class="button link flat margin-bottom-0"
          href="mailto:help@fontawesome.com?subject=I have questions about the terms!"
          target="_blank"
          rel="noopener noreferrer"
        >
          Contact Support
        </a>

        <owa-action-button
          label="Go Make Something Awesome!"
          theme="success"
          :is-full-width="false"
          icon-name="faRocketLaunch"
          icon-style="solid"
          :disabled="!acceptPolicies || isProcessing"
          :is-processing="isProcessing"
          @click="confirmAcceptance"
        />
      </div>
    </div>
  </transition>
</template>

<script>
  import { mapActions, mapGetters } from 'vuex'

  export default {
    name: 'ModalTermsAcceptanceReminder',
    props: {
      isShowing: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        acceptPolicies: false,
        isProcessing: false,
        errorMessage: null,
        optIntoMarketing: false
      }
    },

    computed: {
      ...mapGetters('cookiePreferences', ['isGdprUser']),
      ...mapGetters('emailData', {
        emailDataRecord: 'emailData',
        sendMarketing: 'sendMarketing',
        sendEducation: 'sendEducation',
        sendNews: 'sendNews',
        sendBa: 'sendBa',
        marketingUpdateAt: 'marketingUpdateAt',
        educationUpdateAt: 'educationUpdateAt',
        newsUpdatedAt: 'newsUpdatedAt',
        baUpdatedAt: 'baUpdatedAt'
      }),

      shouldShowMarketingOptIn() {
        return Boolean(this.isGdprUser)
      }
    },

    watch: {
      async isShowing(isShowing) {
        if (isShowing) {
          this.acceptPolicies = false
          this.isProcessing = false
          this.errorMessage = null
          this.optIntoMarketing = false

          await this.initializeMarketingOptIn()
        }
      }
    },

    methods: {
      ...mapActions('accounts', ['updateAccount']),
      ...mapActions('emailData', ['updateEmailData', 'fetchEmailData']),

      async initializeMarketingOptIn() {
        if (!this.shouldShowMarketingOptIn) {
          return
        }

        const hasEmailData = await this.ensureEmailDataLoaded()
        if (!hasEmailData) {
          return
        }

        const currentPreference = this.sendMarketing == null ? false : this.sendMarketing
        this.optIntoMarketing = currentPreference
      },

      async ensureEmailDataLoaded() {
        if (this.emailDataRecord) {
          return true
        }

        try {
          await this.fetchEmailData()
          return true
        } catch (error) {
          console.error('Unable to fetch email data before accepting terms', error)
          return false
        }
      },

      buildEmailPreferenceChanges() {
        if (!this.emailDataRecord) {
          return null
        }

        const desiredOptIn = this.isGdprUser ? Boolean(this.optIntoMarketing) : true
        const nowIso = new Date().toISOString()
        const changes = {}

        const maybeSetPreference = (field, currentValue, alwaysUpdate = false) => {
          if (currentValue == null) {
            changes[field] = desiredOptIn
          } else if (alwaysUpdate && currentValue !== desiredOptIn) {
            changes[field] = desiredOptIn
          }
        }

        const shouldForceMarketingUpdate = this.isGdprUser && this.sendMarketing != null && this.sendMarketing !== desiredOptIn

        maybeSetPreference('send_marketing', this.sendMarketing, shouldForceMarketingUpdate)
        maybeSetPreference('send_education', this.sendEducation)
        maybeSetPreference('send_news', this.sendNews)
        maybeSetPreference('send_ba', this.sendBa)

        const maybeSetTimestamp = (field, currentValue) => {
          if (currentValue == null) {
            changes[field] = nowIso
          }
        }

        maybeSetTimestamp('marketing_update_at', this.marketingUpdateAt)
        maybeSetTimestamp('education_updated_at', this.educationUpdateAt)
        maybeSetTimestamp('news_updated_at', this.newsUpdatedAt)
        maybeSetTimestamp('ba_updated_at', this.baUpdatedAt)

        return Object.keys(changes).length > 0 ? changes : null
      },

      async confirmAcceptance() {
        if (!this.acceptPolicies || this.isProcessing) {
          return
        }

        this.isProcessing = true
        this.errorMessage = null

        try {
          await this.updateAccount({ termsAcceptedAt: true })

          let emailPreferenceChanges = null
          const hasEmailData = await this.ensureEmailDataLoaded()
          if (hasEmailData) {
            emailPreferenceChanges = this.buildEmailPreferenceChanges()
          }

          if (emailPreferenceChanges) {
            await this.updateEmailData(emailPreferenceChanges)
          }

          this.isProcessing = false
        } catch (_error) {
          this.errorMessage = 'We could not save your acceptance. Please try again.'
          this.isProcessing = false
        }
      }
    }
  }
</script>
